<template>
    <div class="page-container">
        <Headbar>
            <template v-slot:left>
                <Button className="--primary --small" v-if="$store.getters.hasPermission('store vehicles')"
                        :onclick="toggleCreateVehicle">{{ $t('vehicles.add_vehicle') }}
                </Button>
                <Button className="--primary --small --outline" v-if="$store.getters.hasPermission('read brands')"
                        :onclick="toggleOpenBrandsIndex">{{ $t('vehicles.vehicles_brands') }}
                </Button>
            </template>
            <template v-slot:right>
                <FormInputSelect v-model="selectedBrand" identifier="brand" :options="brands"
                                 class="select-margin-fix filter-desktop"
                                 :placeholder="$t('brands.brands')" :disabled="is_loading_brands"
                                 @updated="onFilterUpdated"
                                 track-by="id" :display-custom-label="(row) => `${row.attributes.name}`"/>

                <Search class="search-desktop" :placeholder="$t('vehicles.search_vehicles')" @search="search"/>
                <button class="btn-search-mobile btn-icon-only"
                        @click="headbarExpanded = headbarExpanded === 'search' ? null : 'search'">
                    <font-awesome-icon v-if="headbarExpanded === 'search'" :icon="['far', 'times']"/>
                    <font-awesome-icon v-else :icon="['far', 'search']"/>
                </button>
            </template>

            <template v-slot:expanded>
                <div class="headbar-expanded-container">
                    <Search v-if="headbarExpanded === 'search'" :placeholder="$t('vehicles.search_vehicles')"
                            @search="search"/>
                    <FormInputSelect v-model="selectedBrand" identifier="brand" :options="brands"
                                     class="select-margin-fix mobile-margin-fix"
                                     :placeholder="$t('brands.brands')" :disabled="is_loading_brands"
                                     @updated="onFilterUpdated"
                                     track-by="id" :display-custom-label="(row) => `${row.attributes.name}`"
                                     v-if="headbarExpanded === 'search'"/>
                </div>
            </template>
        </Headbar>
        <main>
            <vue-good-table
                mode="remote"
                styleClass="vgt-table vgt-custom"
                :columns="columns"
                :rows="vehicles"
                :isLoading.sync="is_loading_vehicles"
                :search-options="{
                    enabled: false,
                }"
                :pagination-options="{
                    enabled: true,
                    mode: 'records',
                    dropdownAllowAll: false,
                    perPage: 15,
                    perPageDropdownEnabled: false,
                    rowsPerPageLabel: $t('x_per_page', {x: $t('vehicles.vehicles')}),
                }"
                :sort-options="{
                  enabled: true,
                  multipleColumns: true,
                }"
                :totalRows="totalRecords"
                @on-page-change="onPageChange"
                @on-sort-change="onSortChange">
                <template slot="table-row" slot-scope="props">
                    <div v-if="props.column.field === 'attributes.brand'">
                        <p style="text-transform: capitalize;">{{
                                props.row?.relationships?.brand?.data?.attributes?.name
                            }}</p>
                    </div>
                    <div v-else-if="props.column.field === 'attributes.status'">
                        <p style="text-transform: capitalize;">{{ props.row.attributes.status }}</p>
                    </div>
                    <div v-else-if="props.column.field === 'after'" class="td-after">
                        <router-link :to="{name: 'vehicles-update', params: {id: props.row.id}}">
                            <Button
                                v-if="$store.getters.hasAnyPermission(['read vehicles', 'update vehicles'])"
                                className="--secondary --outline --mini --big-text">
                                <font-awesome-icon :icon="['fal', 'pencil']"/>
                            </Button>
                        </router-link>

                        <Button
                            v-if="$store.getters.hasPermission('destroy vehicles')"
                            className="--secondary --outline --mini --big-text"
                            :onclick="()=>toggleDelete(props.row)">
                            <font-awesome-icon :icon="['fal', 'trash']"/>
                        </Button>
                    </div>
                    <span v-else>
                      {{ props.formattedRow[props.column.field] }}
                    </span>
                </template>
            </vue-good-table>
        </main>
    </div>
</template>

<script>
import Headbar from "../../components/headbar/Headbar";
import Search from "../../components/Search";
import Button from "../../components/Button";
import Select from "../../components/form/Select";
import ConfirmModal from "../../components/modal/ConfirmModal";
import VehiclesBrandCreateModal from "../../components/vehicles/VehiclesBrandCreateModal";
import VehiclesCreateModal from "../../components/vehicles/VehiclesCreateModal";
import VehiclesBrandIndexModal from "../../components/vehicles/VehiclesBrandIndexModal";
import FormInputSelect from "../../components/form/FormInputSelect";

export default {
    name: "vehicles-index-page",
    components: {ConfirmModal, Select, Button, Search, Headbar, FormInputSelect},
    data: function () {
        const columns = [
            {
                label: this.$t('vehicles.license_plate'),
                field: 'attributes.license_plate',
                sortable: false,
                width: '14%',
            },
            {
                label: this.$t('vehicles.brand'),
                field: 'attributes.brand',
                sortable: false,
                width: '14%',
            },
            {
                label: this.$t('vehicles.model'),
                field: 'attributes.model',
                sortable: false,
                width: '14%',
            },

            {
                label: this.$t('vehicles.status'),
                field: 'attributes.status',
                sortable: false,
                width: '14%',
            },
            {
                label: this.$t('vehicles.transmission'),
                field: 'attributes.transmission',
                sortable: false,
                width: '14%',
            },
            {
                label: this.$t('vehicles.fuel_type'),
                field: 'attributes.fuel_type',
                sortable: false,
                width: '14%',
            },
            {
                label: this.$t('vehicles.seats'),
                field: 'attributes.seats',
                sortable: false,
                width: '14%',
            }
        ];

        if (this.$store.getters.hasAnyPermission(['update vehicles', 'destroy vehicles']))
            columns.push({
                label: this.$t('actions'),
                field: 'after',
                tdClass: 'td-after',
                sortable: false,
                width: '2%',
            });

        return {
            columns: columns,
            vehicles: [],
            brands: [],
            is_loading_brands: false,
            is_loading_vehicles: false,
            selectedBrand: null,
            totalRecords: null,
            serverParams: {sorting: []},
            searchTerm: null,
            is_deleting: false,
            headbarExpanded: false
        }
    },
    methods: {
        toggleDelete(vehicle) {
            this.$modal.show(
                ConfirmModal, {
                    title: this.$t('vehicles.delete_vehicle'),
                    message: this.$t('vehicles.prompt_delete'),
                    confirmText: this.$t('delete'),
                    cancelText: this.$t('cancel'),
                    confirmClass: '--primary',
                    cancelClass: '--secondary --outline'
                },
                {
                    name: 'confirm-modal',
                    adaptive: true,
                    resizable: true,
                    height: 'auto',
                    scrollable: true,
                    classes: 'modal',
                }, {
                    'before-close': (e) => {
                        if (e.params === true) {
                            this.is_deleting = true;
                            this.$axios.delete(`vehicles/${vehicle.id}`)
                                .then(({data}) => {
                                    this.is_deleting = false;
                                    this.retrieveVehicles();
                                })
                                .catch(e => {
                                    this.is_deleting = false;

                                    this.$notify({
                                        title: this.$t('error'),
                                        text: this.$larerror(e.response.data, this.$t('vehicles.error_delete')),
                                        type: 'error',
                                    });
                                });
                        }
                    }
                }
            );
        },
        toggleCreateVehicle() {
            this.$modal.show(
                VehiclesCreateModal, {},
                {
                    name: 'vehicles-create-modal',
                    adaptive: true,
                    resizable: true,
                    height: 'auto',
                    scrollable: true,
                    classes: 'modal',
                }, {
                    'before-close': (e) => {
                        if (e.params === true)
                            this.retrieveVehicles();
                    }
                }
            );
        },

        toggleOpenBrandsIndex() {
            this.$modal.show(
                VehiclesBrandIndexModal, {},
                {
                    name: 'vehicles-brand-index-modal',
                    adaptive: true,
                    resizable: true,
                    height: 'auto',
                    scrollable: true,
                    classes: 'modal',
                }, {
                    'before-close': (e) => {
                        if (e.params === true)
                            this.retrieveVehicles();
                    }
                }
            );
        },

        onFilterUpdated() {
            const filters = [];

            if (this.selectedBrand) {
                filters.push({
                    filter_by: 'brand_id',
                    filter_value: this.selectedBrand.id
                })
            }

            if (filters.length)
                this.updateParams({filters: filters})
            else
                this.removeParam('filters');

            this.retrieveVehicles();
        },

        updateParams(newProps) {
            this.serverParams = Object.assign({}, this.serverParams, newProps);
        },
        onPageChange(params) {
            this.updateParams({page: params.currentPage});
            this.retrieveVehicles();
        },
        onSortChange(params) {
            const sorts = [];

            if (!params || !params.length) {
                this.updateParams({sorting: []});
                return this.retrieveVehicles();
            }

            params.forEach(p => {
                if (!p.type || p.type === 'none')
                    return;

                let sort_by = null;
                let sort_order = p.type;
                if (p.field === 'attributes.name')
                    sort_by = 'name';
                else if (p.field === 'attributes.email')
                    sort_by = 'email';
                else if (p.field === 'role')
                    sort_by = 'role';
                else
                    sort_by = p.field.split('.')[1];

                sorts.push({sort_order, sort_by})
            });

            this.updateParams({sorting: sorts});
            this.retrieveVehicles();
        },
        removeParam(param) {
            this.$delete(this.serverParams, param);
        },
        search(searchTerm) {
            this.searchTerm = searchTerm;

            if (searchTerm && searchTerm.length)
                this.updateParams({term: searchTerm});
            else this.removeParam('term');

            this.retrieveVehicles();
        },

        retrieveBrands() {
            this.is_loading_brands = true;

            this.$axios.get('brands/list')
                .then(({data}) => {
                    this.brands = data?.data ?? [];
                    this.is_loading_brands = false;
                })
                .catch(e => {
                    this.is_loading_brands = false;

                    this.$notify({
                        title: this.$t('error'),
                        text: this.$larerror(e.response.data, this.$t('brands.error_retrieve')),
                        type: 'error',
                    });
                });
        },

        retrieveVehicles() {
            this.is_loading_vehicles = true;

            const encodedSorting = this.serverParams.sorting.map(f => btoa(JSON.stringify(f)));

            this.$axios.get('vehicles', {params: {...this.serverParams, sorting: encodedSorting}})
                .then(({data}) => {
                    this.vehicles = data?.data ?? [];
                    this.totalRecords = data.meta.total;
                    this.is_loading_vehicles = false;
                })
                .catch(e => {
                    this.is_loading_vehicles = false;

                    this.$notify({
                        title: this.$t('error'),
                        text: this.$larerror(e.response.data, this.$t('vehicles.error_retrieve')),
                        type: 'error',
                    });
                });
        }
    },

    mounted() {
        this.retrieveBrands();
        this.retrieveVehicles();
    },

    head() {
        return {
            title: {
                inner: this.$t('nav.vehicles')
            },
        }
    }
}
</script>

<style lang="scss" scoped>
.page-container {
    .headbar-expanded-container {
        @apply mt-4 flex flex-col gap-y-4 items-center;

        @screen md {
            @apply hidden;
        }
    }

    .select-margin-fix {
        margin-bottom: 0 !important;
        width: 150px !important;
        height: 50px !important;
        min-height: 50px !important;

        &.mobile-margin-fix{
            @apply mx-auto;
            width: 241px !important;
            padding-right: 4px;
        }
    }

    .select-dropdown {
        @apply min-w-40;
    }

    .btn-search-mobile, .btn-filter-mobile {
        @apply block text-primary w-6;

        @screen md {
            @apply hidden;
        }

        &:active, &:focus {
            @apply outline-none;
        }
    }

    .search-desktop, .filter-desktop {
        @apply hidden;

        @screen md {
            @apply block;
        }
    }

    .search-container {
        @apply mx-auto;

        @screen md{
            @apply ml-2;
        }

    }

    .filter-mobile {
        @apply max-w-xs;
    }

    .td-after {
        @apply flex flex-row;

        & > * {
            @apply mr-3;

            &:last-child {
                @apply mr-0;
            }
        }
    }
}
</style>
